import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Network, Loader2, MapPin, Search, Pencil, RefreshCw, Map as MapIcon } from "lucide-react";
import EntityMap from "../components/EntityMap";

const STATS_META = [
  { key: "total", label: "Totale", cls: "text-foreground" },
  { key: "attivi", label: "Attivi", cls: "text-success" },
  { key: "geocoded", label: "Geolocalizzati", cls: "text-primary" },
  { key: "pending", label: "Da geocodificare", cls: "text-amber-500" },
  { key: "failed", label: "Falliti", cls: "text-destructive" },
];

export default function PartnerInstallers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const [busy, setBusy] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (stato) params.set("stato", stato);
      const { data } = await api.get(`/partner-installers?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento");
    } finally { setLoading(false); }
  }, [q, stato]);

  useEffect(() => { load(); }, [load]);

  const syncFromRete = async () => {
    setSyncing(true);
    try {
      await api.post(`/admin/sync-partners`);
      toast.success("Sincronizzazione avviata da rete.torri.solar. I dati si aggiornano tra qualche istante.");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sincronizzazione non riuscita");
    } finally { setSyncing(false); }
  };

  const runGeocoding = async () => {
    setGeocoding(true);
    try {
      let remaining = 1, totalOk = 0;
      while (remaining > 0) {
        const { data } = await api.post(`/partner-installers/geocode-pending?limit=20`);
        totalOk += data.ok;
        remaining = data.remaining;
        setData((d) => d ? { ...d, stats: { ...d.stats, pending: remaining, geocoded: (d.stats.geocoded || 0) + data.ok, failed: (d.stats.failed || 0) + data.failed } } : d);
        if (data.processed === 0) break;
      }
      toast.success(`Geolocalizzazione completata (${totalOk} nuovi)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Geolocalizzazione interrotta");
    } finally { setGeocoding(false); }
  };

  const geocodeOne = async (it) => {
    try {
      const { data } = await api.post(`/partner-installers/${it.id}/geocode`);
      toast[data.geocode_status === "ok" ? "success" : "error"](data.geocode_status === "ok" ? "Geolocalizzato" : "Geolocalizzazione fallita");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = {
        abilitato_calcolo: !!edit.abilitato_calcolo,
        distanza_max_km: edit.distanza_max_km === "" || edit.distanza_max_km == null ? null : Number(edit.distanza_max_km),
        note_interne: edit.note_interne || "",
        logo_url: edit.logo_url || "",
      };
      await api.put(`/partner-installers/${edit.id}`, payload);
      toast.success("Salvato");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const geoBadge = (s) => {
    const map = { ok: ["Geo OK", "border-success/40 text-success"], manual: ["Manuale", "border-primary/40 text-primary"], failed: ["Fallito", "border-destructive/40 text-destructive"] };
    const [label, cls] = map[s] || ["Da geocodificare", "border-amber-500/40 text-amber-500"];
    return <Badge variant="outline" className={`font-normal text-[11px] ${cls}`}>{label}</Badge>;
  };

  const items = data?.items || [];
  const mapPoints = items.filter((i) => i.lat != null && i.lon != null).map((i) => ({
    id: i.id, lat: i.lat, lon: i.lon,
    color: i.attivo ? "#34D399" : "#9CA3AF",
    title: `${i.ragione_sociale}${i.comune ? " — " + i.comune : ""}`,
  }));

  return (
    <div className="space-y-6" data-testid="partner-installers-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Network className="h-6 w-6 text-primary" strokeWidth={1.5} /> Installatori Partner TORRI</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Anagrafiche geolocalizzate usate esclusivamente come riferimento geografico ed economico per la stima dei costi. L'individuazione <b>non costituisce assegnazione della commessa</b>.</p>
          <p className="text-xs text-primary mt-1 flex items-center gap-1"><Network className="h-3 w-3" /> Fonte unica: <b>rete.torri.solar</b> — anagrafica in sola lettura. Modificabili solo i controlli locali (calcolo, distanza max, note, logo override).</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={syncFromRete} disabled={syncing} data-testid="installer-sync-btn"><RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> Sincronizza da rete</Button>
          <Button onClick={runGeocoding} disabled={geocoding || (data?.stats?.pending || 0) === 0} data-testid="installer-geocode-btn">
            {geocoding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />} Geolocalizza {data?.stats?.pending ? `(${data.stats.pending})` : ""}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="installer-stats">
        {STATS_META.map((s) => (
          <Card key={s.key} className="rounded border-border p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className={`text-2xl font-heading font-bold mt-1 ${s.cls}`} data-testid={`stat-${s.key}`}>{data?.stats?.[s.key] ?? "—"}</div>
          </Card>
        ))}
      </div>

      <Card className="rounded border-border p-4" data-testid="installers-map-card">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><MapIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> Mappa geolocalizzazione ({mapPoints.length})</h3>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: "#34D399" }} /> Attivi</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: "#9CA3AF" }} /> Inattivi</span>
          </div>
        </div>
        {mapPoints.length > 0
          ? <EntityMap points={mapPoints} height={380} />
          : <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">Nessun installatore geolocalizzato da mostrare.</div>}
      </Card>

      <Card className="rounded border-border p-4">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca ragione sociale, comune, provincia…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="installer-search" />
          </div>
          <select value={stato} onChange={(e) => setStato(e.target.value)} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="installer-filter-stato">
            <option value="">Tutti gli stati</option>
            <option value="attivo">Attivi</option>
            <option value="inattivo">Inattivi</option>
            <option value="geocode_pending">Da geocodificare</option>
            <option value="geocode_failed">Geocodifica fallita</option>
          </select>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase">Ragione sociale</TableHead>
                  <TableHead className="text-xs uppercase">Comune (Prov.)</TableHead>
                  <TableHead className="text-xs uppercase">Referente</TableHead>
                  <TableHead className="text-xs uppercase">Telefono</TableHead>
                  <TableHead className="text-xs uppercase">Email</TableHead>
                  <TableHead className="text-xs uppercase">Geo</TableHead>
                  <TableHead className="text-xs uppercase text-center">Attivo</TableHead>
                  <TableHead className="text-xs uppercase text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} className="border-border" data-testid={`installer-row-${it.id}`}>
                    <TableCell>
                      <div className="font-medium text-sm">{it.ragione_sociale}</div>
                      <div className="text-xs text-muted-foreground">{it.indirizzo}</div>
                    </TableCell>
                    <TableCell className="text-sm">{it.comune}{it.provincia ? ` (${it.provincia})` : ""}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{it.referente || "—"}</TableCell>
                    <TableCell className="text-xs" data-testid={`installer-tel-${it.id}`}>{it.telefono ? <a href={`tel:${it.telefono.replace(/\s/g, "")}`} className="text-primary hover:underline">{it.telefono}</a> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate" data-testid={`installer-email-${it.id}`}>{it.email ? <a href={`mailto:${it.email}`} className="text-primary hover:underline" title={it.email}>{it.email}</a> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{geoBadge(it.geocode_status)}</TableCell>
                    <TableCell className="text-center"><Badge variant="outline" className={`font-normal text-[11px] ${it.attivo ? "border-success/40 text-success" : "border-muted-foreground/40 text-muted-foreground"}`} data-testid={`installer-attivo-${it.id}`}>{it.attivo ? "Attivo" : "Inattivo"}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => geocodeOne(it)} title="Geolocalizza" data-testid={`installer-geocode-one-${it.id}`}><RefreshCw className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setEdit({ ...it })} title="Impostazioni locali" data-testid={`installer-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">Nessun installatore. Usa "Sincronizza da rete" per popolare l'anagrafica da rete.torri.solar.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Modifica controlli locali (anagrafica read-only da rete) */}
      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent className="max-w-lg" data-testid="installer-edit-dialog">
          <DialogHeader><DialogTitle>Impostazioni installatore</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="rounded border border-border bg-muted/40 p-3 text-xs space-y-0.5" data-testid="edit-readonly-anagrafica">
                <div className="font-medium text-sm text-foreground">{edit.ragione_sociale}</div>
                <div className="text-muted-foreground">{edit.indirizzo}</div>
                <div className="text-muted-foreground">{edit.comune}{edit.provincia ? ` (${edit.provincia})` : ""}{edit.partita_iva ? ` · P.IVA ${edit.partita_iva}` : ""}</div>
                <div className="text-muted-foreground">{[edit.telefono, edit.email].filter(Boolean).join(" · ")}</div>
                <div className="pt-1 text-[10px] uppercase tracking-wide text-primary flex items-center gap-1"><Network className="h-3 w-3" /> Anagrafica da rete.torri.solar — sola lettura</div>
              </div>
              <div className="flex items-center justify-between rounded border border-border p-3">
                <div><Label className="text-sm">Abilitato al calcolo costi</Label><p className="text-[10px] text-muted-foreground">Se disattivo, escluso dalla stima trasferta.</p></div>
                <Switch checked={!!edit.abilitato_calcolo} onCheckedChange={(v) => setEdit({ ...edit, abilitato_calcolo: v })} data-testid="edit-abilitato" />
              </div>
              <div><Label className="text-xs">Distanza massima operativa (km)</Label><Input type="number" value={edit.distanza_max_km ?? ""} onChange={(e) => setEdit({ ...edit, distanza_max_km: e.target.value })} placeholder="es. 150" data-testid="edit-distanza-max" /></div>
              <div><Label className="text-xs">Note interne</Label><Input value={edit.note_interne || ""} onChange={(e) => setEdit({ ...edit, note_interne: e.target.value })} /></div>
              <div><Label className="text-xs">URL logo (override manuale)</Label><Input value={edit.logo_url || ""} onChange={(e) => setEdit({ ...edit, logo_url: e.target.value })} placeholder="https://…/logo.png" data-testid="edit-logo-url" />{edit.logo_url ? <img src={edit.logo_url} alt="logo" className="mt-2 h-10 w-10 rounded object-contain border border-border bg-white" onError={(ev) => { ev.currentTarget.style.display = "none"; }} /> : null}<p className="text-[10px] text-muted-foreground mt-1">Di norma il logo è importato da rete.torri.solar; qui puoi forzarne uno.</p></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="installer-save-edit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
